/**
 * Magic-link share dialog for a single property. Issues / re-issues / revokes
 * the public listing token and offers a rich set of share targets:
 *
 *   – Native share sheet (Web Share API)        – mobile primary CTA
 *   – WhatsApp, Viber, Telegram, SMS, E-Mail    – comm channels
 *   – Facebook, LinkedIn, X / Twitter           – social platforms
 *   – Instagram                                 – clipboard copy + paste hint
 *   – Copy link                                 – fallback
 *
 * All share targets are plain deep links / URL intents — no OAuth, no backend
 * tokens, no per-platform integrations. Each makler shares from their own
 * private profile. The makler can edit the auto-composed German caption before
 * any share action.
 *
 * Backend remains unchanged — magic-link infrastructure (POST/DELETE/GET
 * /properties/:id/share-token) is from TODO 12.6.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Share2,
  Copy,
  MessageCircle,
  Trash2,
  RefreshCw,
  ExternalLink,
  Send,
  Mail,
  Smartphone,
  Facebook,
  Linkedin,
  Twitter,
  Instagram,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { apiFetch } from '@/lib/api';
import { formatDeInteger } from '@/lib/parseDeNumber';
import type { PropertyRow } from '@/hooks/data/useProperties';
import { formatMoney } from '@/lib/formatLocale';

interface Props {
  propertyId: string;
  propertyLabel: string;
  property?: PropertyRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ShareTokenResponse {
  token: string | null;
  url: string | null;
  revoked: boolean;
  revoked_at?: number | null;
}

export function PropertyShareDialog({
  propertyId,
  propertyLabel,
  property,
  open,
  onOpenChange,
}: Props) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const qc = useQueryClient();

  const status = useQuery({
    queryKey: ['property', propertyId, 'share-token'] as const,
    enabled: open,
    queryFn: async () => {
      const res = await apiFetch(`/properties/${propertyId}/share-token`);
      if (!res.ok) throw new Error('share_token_fetch_failed');
      return (await res.json()) as ShareTokenResponse;
    },
  });

  const issue = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/properties/${propertyId}/share-token`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('issue_failed');
      return (await res.json()) as ShareTokenResponse;
    },
    onSuccess: (data) => {
      qc.setQueryData(['property', propertyId, 'share-token'], data);
      toast.success(t('property.share.issued', 'Listing-Link erstellt.'));
    },
  });

  const revoke = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/properties/${propertyId}/share-token`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('revoke_failed');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property', propertyId, 'share-token'] });
      toast.success(t('property.share.revoked', 'Link wurde deaktiviert.'));
    },
  });

  const data = status.data;
  const hasActiveLink = !!data?.url && !data.revoked;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Share2 className="h-4 w-4" />
            {t('property.share.title', 'Listing teilen')}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-ink-2">
          {t(
            'property.share.subtitle',
            'Erstellen Sie einen öffentlichen Magic-Link für diese Immobilie. Empfänger sehen Fotos, KPIs und Ihren Kontakt — keine Verkäuferdaten. Sie können den Link jederzeit deaktivieren.',
          )}
        </p>
        <p className="text-xs text-ink-3">{propertyLabel}</p>

        {status.isLoading ? (
          <p className="py-4 text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
        ) : !hasActiveLink ? (
          <NoLinkPanel
            issuing={issue.isPending}
            wasRevoked={!!data?.revoked}
            onIssue={() => issue.mutate()}
          />
        ) : (
          <ActiveLinkPanel
            url={data.url!}
            propertyLabel={propertyLabel}
            property={property ?? null}
            onRevoke={async () => {
              const ok = await confirm({
                title: t('property.share.confirm_revoke_title', 'Link wirklich deaktivieren?'),
                confirmLabel: t('common.deactivate', 'Deaktivieren'),
                destructive: true,
              });
              if (ok) revoke.mutate();
            }}
            revoking={revoke.isPending}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function NoLinkPanel({
  issuing,
  wasRevoked,
  onIssue,
}: {
  issuing: boolean;
  wasRevoked: boolean;
  onIssue: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3 rounded-md border border-line bg-paper-2 p-4">
      <p className="text-sm">
        {wasRevoked
          ? t('property.share.was_revoked', 'Der vorherige Link wurde deaktiviert. Erstellen Sie einen neuen, um wieder zu teilen.')
          : t('property.share.no_link', 'Für diese Immobilie wurde noch kein Magic-Link erstellt.')}
      </p>
      <Button onClick={onIssue} disabled={issuing}>
        <Share2 className="me-1.5 h-4 w-4" />
        {issuing ? t('common.loading', 'Lädt …') : t('property.share.issue_cta', 'Magic-Link erstellen')}
      </Button>
    </div>
  );
}

// Caption is intentionally always German (primary CRM language). Legacy
// Serbian enum values (preserved from Supabase) are mapped to German labels
// here so the share message never leaks raw `nova_gradnja` / `stara_gradnja`.
const BUILDING_TYPE_DE: Record<string, string> = {
  nova_gradnja: 'Neubau',
  stara_gradnja: 'Altbau',
  neubau: 'Neubau',
  altbau: 'Altbau',
  unknown: '',
};

function germanBuildingType(raw: string | null | undefined): string {
  if (!raw) return '';
  const key = String(raw).trim().toLowerCase();
  const mapped = BUILDING_TYPE_DE[key];
  return mapped ?? raw;
}

function composeCaption(
  property: PropertyRow | null,
  propertyLabel: string,
  url: string,
): string {
  if (!property) {
    return `${propertyLabel}\n\n${url}`;
  }

  const objectType = (property.object_type ?? '').trim();
  const buildingType = germanBuildingType(property.building_type);
  const headLeft = objectType || buildingType || 'Immobilie';

  const cityLine: string[] = [];
  if (property.postal_code) cityLine.push(property.postal_code);
  if (property.city) cityLine.push(property.city);
  const cityDistrict =
    cityLine.join(' ') + (property.district ? ` · ${property.district}` : '');

  const headline =
    cityDistrict.trim().length > 0
      ? `🏠 ${headLeft} in ${cityDistrict.trim()}`
      : `🏠 ${headLeft}`;

  const facts: string[] = [];
  // Show building type as a separate fact when we have an object_type that
  // already filled the headline (avoids "Wohnung Wohnung").
  if (buildingType && objectType && buildingType !== objectType) {
    facts.push(buildingType);
  }
  if (property.property_size && property.property_size > 0) {
    facts.push(`${formatDeInteger(property.property_size)} m²`);
  }
  if (property.rooms_count && String(property.rooms_count).trim().length > 0) {
    facts.push(`${property.rooms_count} Zimmer`);
  }
  if (property.purchase_price && property.purchase_price > 0) {
    facts.push(formatMoney(property.purchase_price));
  }

  const lines: string[] = [headline];
  if (facts.length > 0) lines.push(facts.join(' · '));
  lines.push('');
  lines.push(`Jetzt ansehen: ${url}`);
  lines.push('');
  // 4M positioning — keeps every share reinforcing the same hook. Stays
  // German because the CRM is German-first and the public landing page
  // is rendered in German as well. The OG preview that WhatsApp / Telegram
  // render is built by the /listing/$token Pages Function and carries the
  // same tagline.
  lines.push(
    '✨ 4M Immobilien — Wiens erste digitalisierte Immobilienagentur. Mit KI-Sekretärin Sophie & KI-Assistent Zoki.',
  );
  return lines.join('\n');
}

function ActiveLinkPanel({
  url,
  propertyLabel,
  property,
  onRevoke,
  revoking,
}: {
  url: string;
  propertyLabel: string;
  property: PropertyRow | null;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const { t } = useTranslation();
  const defaultCaption = useMemo(
    () => composeCaption(property, propertyLabel, url),
    [property, propertyLabel, url],
  );
  const [caption, setCaption] = useState<string>(defaultCaption);

  // If the underlying property / URL changes (e.g. token re-issued), reset
  // the caption so the makler is not editing a stale draft.
  useEffect(() => {
    setCaption(defaultCaption);
  }, [defaultCaption]);

  const canNativeShare =
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function';

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('property.share.copied', 'In Zwischenablage kopiert.'));
    } catch {
      toast.error(t('property.share.copy_failed', 'Kopieren fehlgeschlagen.'));
    }
  };

  const onNativeShare = async () => {
    try {
      await navigator.share({
        title: propertyLabel,
        text: caption,
        url,
      });
    } catch (err) {
      // User cancelled — silent
      if ((err as DOMException)?.name === 'AbortError') return;
      toast.error(t('property.share.copy_failed', 'Kopieren fehlgeschlagen.'));
    }
  };

  const onInstagram = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success(
        t(
          'property.share.instagram_copied',
          'Caption kopiert. Öffnen Sie Instagram → Story / DM → Einfügen.',
        ),
      );
    } catch {
      toast.error(t('property.share.copy_failed', 'Kopieren fehlgeschlagen.'));
    }
  };

  // Each share target reads `caption` at click time so makler edits are
  // picked up without a re-render of the buttons.
  const captionEnc = encodeURIComponent(caption);
  const urlEnc = encodeURIComponent(url);
  const titleEnc = encodeURIComponent(propertyLabel);

  const whatsappUrl = `https://wa.me/?text=${captionEnc}`;
  const viberUrl = `viber://forward?text=${captionEnc}`;
  const telegramUrl = `https://t.me/share/url?url=${urlEnc}&text=${captionEnc}`;
  const smsUrl = `sms:?&body=${captionEnc}`;
  const mailUrl = `mailto:?subject=${titleEnc}&body=${captionEnc}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${urlEnc}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${urlEnc}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${captionEnc}`;

  return (
    <div className="space-y-4">
      {/* The URL */}
      <div className="space-y-1.5">
        <p className="text-[11px] uppercase tracking-[0.08em] text-ink-3">
          {t('property.share.link_label', 'Öffentlicher Link')}
        </p>
        <div className="flex items-center gap-2 rounded-md border border-line bg-paper-2 px-3 py-2">
          <code className="min-w-0 flex-1 truncate font-mono text-xs">{url}</code>
          <Button size="sm" variant="ghost" onClick={onCopyLink} title={t('common.copy', 'Kopieren')}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={t('property.share.preview', 'Vorschau')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-2 hover:bg-paper-3"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      {/* Editable caption */}
      <div className="space-y-1.5">
        <p className="text-[11px] uppercase tracking-[0.08em] text-ink-3">
          {t('property.share.caption_label', 'Nachricht / Caption')}
        </p>
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          rows={5}
          placeholder={t(
            'property.share.caption_placeholder',
            'Schreiben Sie einen kurzen Begleittext …',
          )}
          className="w-full rounded-md border border-line bg-paper-1 px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
        />
        <p className="text-[11px] text-ink-3">
          {t(
            'property.share.caption_hint',
            'Wird beim Teilen mitgesendet. Sie können den Text vor jedem Senden anpassen.',
          )}
        </p>
      </div>

      {/* Native share — primary CTA on mobile */}
      {canNativeShare && (
        <Button onClick={onNativeShare} className="w-full" size="lg">
          <Share2 className="me-1.5 h-4 w-4" />
          {t('property.share.via_native', 'Über System teilen …')}
        </Button>
      )}

      {/* Platform grid */}
      <div>
        <p className="mb-2 text-[11px] uppercase tracking-[0.08em] text-ink-3">
          {t('property.share.platforms_label', 'Plattformen & Kanäle')}
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <PlatformButton
            href={whatsappUrl}
            color="#25D366"
            label={t('property.share.via_whatsapp', 'WhatsApp')}
            icon={<MessageCircle className="h-4 w-4" />}
          />
          <PlatformButton
            href={viberUrl}
            color="#7360F2"
            label={t('property.share.via_viber', 'Viber')}
            icon={<MessageCircle className="h-4 w-4" />}
          />
          <PlatformButton
            href={telegramUrl}
            color="#0088CC"
            label={t('property.share.via_telegram', 'Telegram')}
            icon={<Send className="h-4 w-4" />}
          />
          <PlatformButton
            href={smsUrl}
            color="#6b7280"
            label={t('property.share.via_sms', 'SMS')}
            icon={<Smartphone className="h-4 w-4" />}
          />
          <PlatformButton
            href={mailUrl}
            color="#6b7280"
            label={t('property.share.via_email', 'E-Mail')}
            icon={<Mail className="h-4 w-4" />}
          />
          <PlatformButton
            href={facebookUrl}
            color="#1877F2"
            label={t('property.share.via_facebook', 'Facebook')}
            icon={<Facebook className="h-4 w-4" />}
          />
          <PlatformButton
            href={linkedinUrl}
            color="#0A66C2"
            label={t('property.share.via_linkedin', 'LinkedIn')}
            icon={<Linkedin className="h-4 w-4" />}
          />
          <PlatformButton
            href={twitterUrl}
            color="#0f172a"
            label={t('property.share.via_twitter', 'X / Twitter')}
            icon={<Twitter className="h-4 w-4" />}
          />
          <button
            type="button"
            onClick={onInstagram}
            className="col-span-2 inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 sm:col-span-2"
            style={{
              background:
                'linear-gradient(135deg, #f58529 0%, #dd2a7b 45%, #8134af 75%, #515bd4 100%)',
            }}
          >
            <Instagram className="h-4 w-4" />
            {t('property.share.via_instagram', 'Instagram (Caption kopieren)')}
          </button>
          <button
            type="button"
            onClick={onCopyLink}
            className="col-span-2 inline-flex items-center justify-center gap-2 rounded-md border border-line bg-paper-1 px-3 py-2 text-sm font-semibold text-ink-1 transition-colors hover:bg-paper-3 sm:col-span-2"
          >
            <Copy className="h-4 w-4" />
            {t('property.share.copy_link', 'Link kopieren')}
          </button>
        </div>
      </div>

      {/* DSGVO note */}
      <p className="text-[11px] leading-relaxed text-ink-3">
        {t(
          'property.share.dsgvo_note',
          'Hinweis: Die öffentliche Seite zeigt keine Verkäuferdaten — nur Fotos, KPIs und Ihren Kontakt. Anfragen erstellen automatisch einen Interessent-Eintrag im CRM.',
        )}
      </p>

      {/* Revoke */}
      <div className="flex items-center justify-between rounded-md border border-dashed border-line bg-paper-2 px-3 py-2 text-xs">
        <span className="text-ink-3">
          {t('property.share.revoke_hint', 'Link nicht mehr benötigt?')}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRevoke}
          disabled={revoking}
          className="text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"
        >
          {revoking ? (
            <RefreshCw className="me-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="me-1.5 h-3.5 w-3.5" />
          )}
          {t('property.share.revoke_cta', 'Deaktivieren')}
        </Button>
      </div>
    </div>
  );
}

function PlatformButton({
  href,
  color,
  label,
  icon,
}: {
  href: string;
  color: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
      style={{ background: color }}
    >
      {icon}
      {label}
    </a>
  );
}
